import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/EngagementPostComments.graphql';
import { Query, QueryengagementPostCommentsArgs } from '../../__generated__/globalTypes';

export const useEngagementPostCommentsQuery = (options?: QueryHookOptions<Query, QueryengagementPostCommentsArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryengagementPostCommentsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useEngagementPostCommentsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryengagementPostCommentsArgs>
): QueryResult<Query, QueryengagementPostCommentsArgs> & {
  getEngagementPostComments: LazyQueryExecFunction<Query, QueryengagementPostCommentsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getEngagementPostComments, result] = useLazyQuery<Query, QueryengagementPostCommentsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getEngagementPostComments, ...result };
};

export const useEngagementPostCommentsPromiseQuery = () => {
  const client = useApolloClient();

  const getEngagementPostComments = useCallback(
    (options?: Omit<QueryOptions<QueryengagementPostCommentsArgs, Query>, 'query'>) =>
      client.query<Query, QueryengagementPostCommentsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getEngagementPostComments };
};
// Suspense Query Hooks
export const useEngagementPostCommentsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryengagementPostCommentsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryengagementPostCommentsArgs>(QUERY, {
    ...options
  });

  return result;
};
